import {
  ArrowUpRight,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Plus,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import {
  ColumnPicker,
  tableMinWidthClass,
  type ColumnOption,
} from '@/components/common/ColumnPicker';
import { DateRangeFilterDialog, type DateRange } from '@/components/sales/DateRangeFilterDialog';
import { FilterGroup } from '@/components/sales/FilterGroup';
import { SalesTableState } from '@/components/sales/SalesTableState';
import { SortDialog, type SortOption } from '@/components/sales/SortDialog';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import { UpgradePlanDialog } from '@/components/sales/UpgradePlanDialog';
import LeadWizard from '@/components/sales/wizard/LeadWizard';
import { useToast } from '@/components/ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SETTLED_PAYMENT_MODES } from '@/features/sales-rm/leadForm';
import useClampPage from '@/hooks/useClampPage';
import useDebounce from '@/hooks/useDebounce';
import { useLead, useInvalidateLeads } from '@/hooks/useLeads';
import {
  useCreateAddOnOrder,
  useOrder,
  useOrdersList,
  usePatchDueInstallment,
} from '@/hooks/useOrders';
import usePersistedState from '@/hooks/usePersistedState';
import { useTableColumns } from '@/hooks/useTableColumns';
import { formatValidTill, inr, kydLabel, kydTone } from '@/lib/sales-metrics';
import { type OrderListItem } from '@/services/orders.service';
import { errorMessage } from '@/utils/apiError';
import { ROUTES } from '@/utils/constants';

type SortKey =
  | 'soldAt'
  | 'clientName'
  | 'arn'
  | 'orderNumber'
  | 'paymentStatus'
  | 'paymentAmount'
  | 'nextDueDate';

const SORT_OPTIONS: SortOption<SortKey>[] = [
  { key: 'soldAt', label: 'Order Date' },
  { key: 'clientName', label: 'Client Name' },
  { key: 'arn', label: 'ARN' },
  { key: 'orderNumber', label: 'Order No.' },
  { key: 'paymentStatus', label: 'Payment Status' },
  { key: 'paymentAmount', label: 'Amount' },
  { key: 'nextDueDate', label: 'Due Date' },
];

const TYPE_OPTIONS = ['Initial', 'Add-on', 'Upgrade'];
const PAY_OPTIONS = ['Not Paid', 'Partial Payment', 'Full Payment'];
// Orders carrying at least one Due installment - money promised but not yet
// received, with service already running (ADR-053).
const DUE_OPTIONS = ['Has Due Installments'];
const ALL_ROWS = 1000;
type OrderColumnKey =
  | 'order'
  | 'soldAt'
  | 'client'
  | 'brandName'
  | 'arn'
  | 'mobile'
  | 'city'
  | 'kydCompliant'
  | 'arnValidTill'
  | 'products'
  | 'paymentStatus'
  | 'due'
  | 'amount'
  | 'status';

// Order here is the order of the table AND of the picker's checkbox list. The
// Actions column is absent because it is not a column of data.
const ORDER_COLUMNS: readonly ColumnOption<OrderColumnKey>[] = [
  { key: 'order', label: 'Order' },
  { key: 'soldAt', label: 'Order Date' },
  { key: 'client', label: 'Client' },
  { key: 'brandName', label: 'Brand Name' },
  { key: 'arn', label: 'ARN' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'city', label: 'City' },
  { key: 'kydCompliant', label: 'KYD Compliant' },
  { key: 'arnValidTill', label: 'ARN Valid Date' },
  { key: 'products', label: 'Products' },
  { key: 'paymentStatus', label: 'Payment Status' },
  { key: 'due', label: 'Due' },
  { key: 'amount', label: 'Amount' },
  { key: 'status', label: 'Status' },
];

const ORDER_COLUMN_KEYS = ORDER_COLUMNS.map((column) => column.key);

// The ones that identify WHICH order a row is. Hiding any of them leaves rows
// an RM cannot tell apart, so they are pinned on rather than offered as a
// choice. (There is no email column on this table; the lead's email lives on
// the order detail page.)
const ORDER_LOCKED_COLUMNS: readonly OrderColumnKey[] = [
  'order',
  'client',
  'brandName',
  'arn',
  'mobile',
];

const ROWS_PER_PAGE_OPTIONS: { value: number; label: string }[] = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: ALL_ROWS, label: 'All' },
];

const payTone = (s: string): ChipTone =>
  s === 'Full Payment' ? 'success' : s === 'Partial Payment' ? 'warning' : 'neutral';

const fmtDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })
    : '-';

// A bare YYYY-MM-DD, parsed as a LOCAL date so it never shifts a day.
const fmtDateOnly = (isoDate: string | null): string => {
  if (!isoDate) return '-';
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
};

/**
 * Editor for one order's Due installments (ADR-053).
 *
 * Two things can be done to a Due row and they are deliberately separate
 * actions: move the promised date, or record the payment that settles it.
 * Neither can change an amount, so the schedule's "installments must total the
 * payable amount" invariant cannot be broken from here.
 */
interface DueInstallmentsDialogProps {
  order: OrderListItem | null;
  onClose: () => void;
}

const DueInstallmentsDialog = ({ order, onClose }: DueInstallmentsDialogProps) => {
  const { toast } = useToast();
  const patch = usePatchDueInstallment();
  // Keyed by installment number so two rows never share draft state.
  const [dates, setDates] = useState<Record<number, string>>({});
  const [modes, setModes] = useState<Record<number, string>>({});
  const [txns, setTxns] = useState<Record<number, string>>({});

  // Re-sync the date drafts whenever the PERSISTED dates change - on open, and
  // again after a save lands. Keyed on the dates themselves rather than the
  // order object, whose identity changes on every refetch and would otherwise
  // wipe a half-typed transaction number.
  const persistedDates = (order?.dueInstallments ?? [])
    .map((d) => `${d.installmentNumber}:${d.dueDate}`)
    .join('|');

  useEffect(() => {
    setDates(
      Object.fromEntries(
        persistedDates
          ? persistedDates.split('|').map((entry) => {
              const [num, date] = entry.split(':');
              return [Number(num), date];
            })
          : [],
      ),
    );
  }, [persistedDates]);

  if (!order) return null;

  const saveDate = (installmentNumber: number) => {
    patch.mutate(
      { orderId: order.id, installmentNumber, payload: { dueDate: dates[installmentNumber] } },
      {
        onSuccess: () => toast.success(`Installment #${installmentNumber} due date updated`),
        onError: (err) => toast.error(errorMessage(err, 'Could not update the due date')),
      },
    );
  };

  const settle = (installmentNumber: number) => {
    const mode = modes[installmentNumber];
    const transactionNo = txns[installmentNumber]?.trim();
    if (!mode || !transactionNo) {
      toast.error('Enter both the payment mode and the transaction number');
      return;
    }
    patch.mutate(
      { orderId: order.id, installmentNumber, payload: { mode, transactionNo } },
      {
        onSuccess: () => {
          toast.success(`Installment #${installmentNumber} marked as paid`);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not record the payment')),
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-h-[85vh] w-[95vw] flex-col overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Due Installments</DialogTitle>
          <DialogDescription>
            {`${order.clientName} · Order #${order.orderNumber} · ${inr(order.dueAmount)} promised`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {order.dueInstallments.map((d) => (
            <div
              key={d.installmentNumber}
              className="rounded-lg border border-border bg-muted/30 p-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-foreground">
                  {`Installment #${d.installmentNumber} · ${inr(d.amount)}`}
                </p>
                <StatusChip tone={d.handedOver ? 'info' : 'warning'}>
                  {d.handedOver ? 'Due (with billing)' : 'Due'}
                </StatusChip>
              </div>

              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Promised date</Label>
                  <Input
                    type="date"
                    value={dates[d.installmentNumber] ?? d.dueDate}
                    onChange={(e) =>
                      setDates((p) => ({ ...p, [d.installmentNumber]: e.target.value }))
                    }
                    className="h-8 w-40"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    patch.isPending || (dates[d.installmentNumber] ?? d.dueDate) === d.dueDate
                  }
                  onClick={() => saveDate(d.installmentNumber)}
                >
                  Move date
                </Button>
              </div>

              <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Payment mode</Label>
                  <Select
                    value={modes[d.installmentNumber] ?? ''}
                    onChange={(e) =>
                      setModes((p) => ({ ...p, [d.installmentNumber]: e.target.value }))
                    }
                    className="h-8 w-36"
                    aria-label={`Installment ${d.installmentNumber} payment mode`}
                  >
                    <option value="">- Select -</option>
                    {SETTLED_PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Transaction no.</Label>
                  <Input
                    value={txns[d.installmentNumber] ?? ''}
                    maxLength={100}
                    placeholder="e.g. TXN123456"
                    onChange={(e) =>
                      setTxns((p) => ({ ...p, [d.installmentNumber]: e.target.value }))
                    }
                    className="h-8 w-40"
                  />
                </div>
                <Button
                  size="sm"
                  disabled={patch.isPending}
                  onClick={() => settle(d.installmentNumber)}
                >
                  <Check className="mr-1 h-3.5 w-3.5" />
                  Mark paid
                </Button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground">
          Amounts cannot be changed here - they are what billing invoiced against. Recording a
          payment settles the installment and it stops being due.
        </p>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Sales → Orders (ADR-048). One row per PURCHASE, not per client: a client who
 * has bought three times is three rows, each with its own products, payment and
 * sale date. Search matches the client, so all of one person's orders come back
 * together.
 *
 * The open order lives in the URL (`/sales-rm/sales/:orderId`) rather than in
 * component state, so the breadcrumb's "Sales" crumb is a real navigation that
 * actually returns to the list.
 */
const SalesOrdersPage = () => {
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams<{ orderId?: string }>();
  const openOrderId = orderIdParam ? Number(orderIdParam) : null;

  const [searchInput, setSearchInput] = usePersistedState('salesOrders:search', '');
  const search = useDebounce(searchInput, 350);
  const [dateRange, setDateRange] = usePersistedState<DateRange | null>(
    'salesOrders:dateRange',
    null,
  );
  const [sortBy, setSortBy] = usePersistedState<SortKey | undefined>(
    'salesOrders:sortBy',
    undefined,
  );
  const [sortDir, setSortDir] = usePersistedState<'asc' | 'desc'>('salesOrders:sortDir', 'desc');
  const [typeFilter, setTypeFilter] = usePersistedState<string[]>('salesOrders:typeFilter', []);
  const [payFilter, setPayFilter] = usePersistedState<string[]>('salesOrders:payFilter', []);
  const [dueFilter, setDueFilter] = usePersistedState<string[]>('salesOrders:dueFilter', []);
  // Which order's Due installments are open for editing (null = closed). Held
  // as an ID, not the row object: moving a date refetches the list, and a
  // captured object would keep showing the date that was just replaced.
  const [dueEditOrderId, setDueEditOrderId] = useState<number | null>(null);
  // Which columns this table shows, persisted alongside the filters above.
  const columns = useTableColumns<OrderColumnKey>(
    'salesOrders:columns',
    ORDER_COLUMN_KEYS,
    ORDER_LOCKED_COLUMNS,
  );
  const [page, setPage] = usePersistedState('salesOrders:page', 1);
  const [limit, setLimit] = usePersistedState('salesOrders:limit', 25);

  const {
    data: orders = [],
    isLoading,
    isError,
    refetch,
  } = useOrdersList({ search: search || undefined, from: dateRange?.from, to: dateRange?.to });

  const invalidateLeads = useInvalidateLeads();
  const addOnOrder = useCreateAddOnOrder();
  const { toast } = useToast();
  // Which client the Upgrade Plan picker is open for (null = closed).
  const [upgradeFor, setUpgradeFor] = useState<{ leadId: number; clientName: string } | null>(null);

  // Fetched by id rather than looked up in the list: a just-created order is
  // opened before the list has refetched, and an order reached by URL may not be
  // on the current page of results at all.
  const { data: openOrder, isLoading: orderLoading, isError: orderError } = useOrder(openOrderId);
  // `?orderId=` makes the DTO describe THAT purchase, not the client's latest -
  // opening an older completed order must not render the newest one's cart.
  const { data: leadForWizard, isLoading: leadLoading } = useLead(
    openOrder?.leadId ?? null,
    openOrderId,
  );

  const { setExtra } = useBreadcrumb();
  useEffect(() => {
    setExtra(openOrder ? `Order #${openOrder.orderNumber}` : null);
    return () => setExtra(null);
  }, [openOrder, setExtra]);

  const activeFilters = typeFilter.length + payFilter.length + dueFilter.length;

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const clearSort = () => {
    setSortBy(undefined);
    setSortDir('desc');
    setPage(1);
  };

  const filteredSorted = useMemo(() => {
    let result = orders;
    if (typeFilter.length) result = result.filter((o) => typeFilter.includes(o.orderType));
    if (payFilter.length) result = result.filter((o) => payFilter.includes(o.paymentStatus));
    if (dueFilter.length) result = result.filter((o) => o.dueInstallments.length > 0);
    if (sortBy) {
      result = [...result].sort((a, b) => {
        const av = a[sortBy];
        const bv = b[sortBy];
        // Orders with nothing due sort last under a Due-date sort, whichever way
        // it runs - an empty date is not "earliest".
        if (sortBy === 'nextDueDate' && (av == null || bv == null)) {
          if (av == null && bv == null) return 0;
          return av == null ? 1 : -1;
        }
        const cmp =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av ?? '').localeCompare(String(bv ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [orders, typeFilter, payFilter, dueFilter, sortBy, sortDir]);

  const total = filteredSorted.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  useClampPage(page, totalPages, !isLoading, setPage);
  const fromRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const toRow = Math.min(total, page * limit);
  const pageRows = filteredSorted.slice((page - 1) * limit, (page - 1) * limit + limit);

  // Opens a NEW order for an existing client, then routes straight to it so the
  // wizard starts at Step 1 with an empty cart.
  const handleAddProducts = (row: OrderListItem) => {
    addOnOrder.mutate(row.leadId, {
      onSuccess: (order) => {
        toast.success(`Order #${order.orderNumber} started for ${row.clientName}`);
        void refetch();
        navigate(`${ROUTES.SALES_RM_ORDERS}/${order.id}`);
      },
      onError: (err) => toast.error(errorMessage(err, 'Could not start a new order')),
    });
  };

  const handleUpgradeCreated = (orderId: number) => {
    void refetch();
    void navigate(`${ROUTES.SALES_RM_ORDERS}/${orderId}`);
  };

  const exportCsv = () => {
    const headers = [
      'Order',
      'Type',
      'Order Date',
      'Client ID',
      'ARN',
      'Client',
      'Mobile',
      'Email',
      'City',
      'Products',
      'Payment Status',
      'Amount',
      'Confirmed On',
    ];
    const escape = (v: string): string => `"${v.replace(/"/g, '""')}"`;
    const lines = filteredSorted.map((o) =>
      [
        `#${o.orderNumber}`,
        o.orderType,
        fmtDate(o.soldAt),
        String(o.leadCode),
        o.arn,
        o.clientName,
        o.mobile,
        o.email,
        o.city ?? '',
        o.products.map((p) => p.productName).join('; '),
        o.paymentStatus,
        String(o.paymentAmount),
        fmtDate(o.onboardedAt),
      ]
        .map(escape)
        .join(','),
    );
    const csv = [headers.map(escape).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const backToList = () => {
    invalidateLeads();
    void refetch();
    navigate(ROUTES.SALES_RM_ORDERS);
  };

  // ── Wizard view: one specific order ─────────────────────────────────────────
  if (openOrderId != null) {
    if (orderLoading || leadLoading) {
      return (
        <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
          Loading order…
        </div>
      );
    }
    if (orderError || !leadForWizard) {
      return (
        <SalesTableState
          variant="error"
          title="Order not found"
          description="This order no longer exists, or you don't have access to it."
          onRetry={backToList}
        />
      );
    }
    return (
      <LeadWizard
        lead={leadForWizard}
        onBack={backToList}
        onOnboarded={backToList}
        backLabel="Back to orders"
      />
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-3 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search
            aria-hidden="true"
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="Search orders by client name, ARN, ID or email"
            placeholder="Search by client name, ARN, ID or email…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SortDialog
            options={SORT_OPTIONS}
            sortBy={sortBy}
            sortDir={sortDir}
            onChange={toggleSort}
            onClear={clearSort}
          />

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Filter className="h-4 w-4" />
                Filters
                {activeFilters > 0 && (
                  <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                    {activeFilters}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-3">
              <FilterGroup
                label="Order Type"
                options={TYPE_OPTIONS}
                selected={typeFilter}
                onChange={(v) => {
                  setTypeFilter(v);
                  setPage(1);
                }}
              />
              <FilterGroup
                label="Payment Status"
                options={PAY_OPTIONS}
                selected={payFilter}
                onChange={(v) => {
                  setPayFilter(v);
                  setPage(1);
                }}
              />
              <FilterGroup
                label="Due"
                options={DUE_OPTIONS}
                selected={dueFilter}
                onChange={(v) => {
                  setDueFilter(v);
                  setPage(1);
                }}
              />
              {activeFilters > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setTypeFilter([]);
                    setPayFilter([]);
                    setDueFilter([]);
                    setPage(1);
                  }}
                >
                  Clear all
                </Button>
              )}
            </PopoverContent>
          </Popover>

          <DateRangeFilterDialog
            value={dateRange}
            onApply={(r) => {
              setDateRange(r);
              setPage(1);
            }}
          />

          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={exportCsv}
            disabled={filteredSorted.length === 0}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <div className="w-20">
            <Select
              aria-label="Rows per page"
              value={String(limit)}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ColumnPicker columns={ORDER_COLUMNS} api={columns} tableLabel="orders table" />
          <span className="text-sm text-muted-foreground">{`${fromRow}–${toRow} of ${total}`}</span>
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {isLoading ? (
          <div className="flex h-full min-h-[320px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading orders…
          </div>
        ) : isError ? (
          <SalesTableState
            variant="error"
            title="Couldn't load orders"
            description="Something went wrong while fetching orders. Please try again."
            onRetry={() => refetch()}
          />
        ) : total === 0 ? (
          <SalesTableState
            variant="empty"
            title="No orders yet"
            description="Orders appear here once a lead reaches the Billing Handover step."
          />
        ) : (
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-hidden">
            <Table
              containerClassName="max-h-[70dvh] lg:max-h-full"
              className={tableMinWidthClass(columns.hiddenCount)}
            >
              <TableHeader>
                <TableRow className="whitespace-nowrap">
                  {columns.isVisible('order') && <TableHead>Order</TableHead>}
                  {columns.isVisible('soldAt') && <TableHead>Order Date</TableHead>}
                  {columns.isVisible('client') && <TableHead>Client</TableHead>}
                  {columns.isVisible('brandName') && <TableHead>Brand Name</TableHead>}
                  {columns.isVisible('arn') && <TableHead>ARN</TableHead>}
                  {columns.isVisible('mobile') && <TableHead>Mobile</TableHead>}
                  {columns.isVisible('city') && <TableHead>City</TableHead>}
                  {columns.isVisible('kydCompliant') && <TableHead>KYD Compliant</TableHead>}
                  {columns.isVisible('arnValidTill') && <TableHead>ARN Valid Date</TableHead>}
                  {columns.isVisible('products') && <TableHead>Products</TableHead>}
                  {columns.isVisible('paymentStatus') && <TableHead>Payment Status</TableHead>}
                  {columns.isVisible('due') && <TableHead>Due</TableHead>}
                  {columns.isVisible('amount') && (
                    <TableHead className="text-right">Amount</TableHead>
                  )}
                  {columns.isVisible('status') && <TableHead>Status</TableHead>}
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((o) => {
                  const names = o.products.map((p) => p.productName);
                  return (
                    <TableRow
                      key={o.id}
                      className="cursor-pointer whitespace-nowrap"
                      onClick={() => {
                        void navigate(`${ROUTES.SALES_RM_ORDERS}/${o.id}`);
                      }}
                    >
                      {columns.isVisible('order') && (
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            <Link
                              to={`${ROUTES.SALES_RM_ORDERS}/${o.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <span className="sr-only">{`Open order for ${o.clientName}: `}</span>
                              {`#${o.orderNumber}`}
                            </Link>
                            {o.orderType === 'Add-on' && (
                              <Badge variant="secondary" className="font-normal">
                                Add-on
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {columns.isVisible('soldAt') && <TableCell>{fmtDate(o.soldAt)}</TableCell>}
                      {columns.isVisible('client') && <TableCell>{o.clientName}</TableCell>}
                      {columns.isVisible('brandName') && (
                        <TableCell>
                          <div className="max-w-[12rem] truncate" title={o.brandName ?? ''}>
                            {o.brandName || '-'}
                          </div>
                        </TableCell>
                      )}
                      {columns.isVisible('arn') && <TableCell>{o.arn}</TableCell>}
                      {/* Explicit tel: link - index.html sets
                          `format-detection: telephone=no`, so a number is only
                          dialable if we make it one. The 44px touch target is
                          `sm:`-dropped (the convention noted on buttonVariants):
                          unconditional, it was the tallest thing in every row and
                          set the row height for the whole table on desktop. */}
                      {columns.isVisible('mobile') && (
                        <TableCell>
                          {o.mobile ? (
                            <a
                              href={`tel:${o.mobile.replace(/[\s-]/g, '')}`}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Call ${o.clientName} on ${o.mobile}`}
                              className="inline-flex min-h-11 items-center rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0"
                            >
                              {o.mobile}
                            </a>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      )}
                      {columns.isVisible('city') && <TableCell>{o.city ?? '-'}</TableCell>}
                      {/* AMFI facts, read-only everywhere - only Fetch from ARN
                          in wizard Step 1 can change what they say. */}
                      {columns.isVisible('kydCompliant') && (
                        <TableCell>
                          <StatusChip tone={kydTone(o.kydCompliant)}>
                            {kydLabel(o.kydCompliant)}
                          </StatusChip>
                        </TableCell>
                      )}
                      {columns.isVisible('arnValidTill') && (
                        <TableCell>{formatValidTill(o.arnValidTill)}</TableCell>
                      )}
                      {columns.isVisible('products') && (
                        <TableCell>
                          {names.length === 0 ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <div className="flex items-center gap-1" title={names.join(', ')}>
                              <Badge
                                variant="secondary"
                                className="max-w-[180px] truncate font-normal"
                              >
                                {names[0]}
                              </Badge>
                              {names.length > 1 && (
                                <span className="shrink-0 text-xs text-muted-foreground">{`+${names.length - 1}`}</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      )}
                      {columns.isVisible('paymentStatus') && (
                        <TableCell>
                          <StatusChip tone={payTone(o.paymentStatus)}>{o.paymentStatus}</StatusChip>
                        </TableCell>
                      )}
                      {/* Money promised but not received. Clicking it opens the
                          editor, so a promised date can be moved or settled
                          without reopening the wizard (ADR-053). */}
                      {columns.isVisible('due') && (
                        <TableCell>
                          {o.dueInstallments.length === 0 ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDueEditOrderId(o.id);
                              }}
                              title={`${o.dueInstallments.length} due installment(s) totalling ${inr(o.dueAmount)}`}
                            >
                              <CalendarClock className="h-3.5 w-3.5 text-warning" />
                              {fmtDateOnly(o.nextDueDate)}
                              {o.dueInstallments.length > 1 && (
                                <span className="text-xs text-muted-foreground">
                                  {`+${o.dueInstallments.length - 1}`}
                                </span>
                              )}
                            </Button>
                          )}
                        </TableCell>
                      )}
                      {columns.isVisible('amount') && (
                        <TableCell className="text-right tabular-nums">
                          {inr(o.paymentAmount)}
                        </TableCell>
                      )}
                      {columns.isVisible('status') && (
                        <TableCell>
                          <StatusChip tone={o.inProgress ? 'warning' : 'success'}>
                            {o.inProgress ? 'In Progress' : 'Confirmed'}
                          </StatusChip>
                        </TableCell>
                      )}
                      {/* Sells more products to this client as a NEW order, so the
                          earlier sale's prices, schedule and renewal dates are
                          untouched (ADR-048). */}
                      <TableCell>
                        <div className="flex gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 max-lg:size-11 max-lg:px-0"
                            aria-label={`Add products to order #${o.orderNumber}`}
                            disabled={addOnOrder.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddProducts(o);
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            <span className="max-lg:hidden">Add Products</span>
                          </Button>
                          {/* Moves the client's live MFBOX plan up a tier. A
                              separate flow from Add Products: it supersedes the
                              current plan rather than selling alongside it, and
                              is priced as the prorated difference. */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 max-lg:size-11 max-lg:px-0"
                            aria-label={`Upgrade plan for ${o.clientName}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setUpgradeFor({ leadId: o.leadId, clientName: o.clientName });
                            }}
                          >
                            <ArrowUpRight className="h-3.5 w-3.5" />
                            <span className="max-lg:hidden">Upgrade Plan</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {upgradeFor && (
        <UpgradePlanDialog
          open
          onOpenChange={(next) => {
            if (!next) setUpgradeFor(null);
          }}
          leadId={upgradeFor.leadId}
          clientName={upgradeFor.clientName}
          onCreated={handleUpgradeCreated}
        />
      )}

      {/* Resolved from the live list, so a moved date is reflected as soon as
          the refetch lands. Keyed on the order so switching rows resets the
          drafts inside. */}
      <DueInstallmentsDialog
        key={dueEditOrderId ?? 'none'}
        order={orders.find((o) => o.id === dueEditOrderId) ?? null}
        onClose={() => setDueEditOrderId(null)}
      />
    </div>
  );
};

export default SalesOrdersPage;
